import { ShieldCheck, Heart } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { OptInForm } from "@/components/OptInForm";

const SOFIA_IMG =
  "https://customer-assets-4nw71qhi.emergentagent.net/wingman/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/attachments/5c1325956ab0455cb074e056a198923b_image.bin";

const CAUSE_FIELDS = [
  { name: "first_name", label: "First Name", type: "text" },
  { name: "last_name", label: "Last Name", type: "text" },
  { name: "email", label: "Email Address", type: "email", full: true },
  { name: "cause_name", label: "Cause Name", type: "text", full: true },
  { name: "description", label: "Brief Description", textarea: true, full: true },
];

export default function Causes() {
  return (
    <div data-testid="causes-page">
      <section className="mx-auto max-w-7xl px-4 pb-20 pt-14 sm:px-6 lg:px-8 lg:pt-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-start">
          <Reveal className="lg:col-span-7">
            <Eyebrow>For causes</Eyebrow>
            <h1
              className="mt-4 max-w-3xl font-serif text-4xl font-bold leading-[1.15] tracking-tight text-ink sm:text-5xl lg:text-6xl"
              data-testid="causes-headline"
            >
              Every cause has a story.{" "}
              <span className="italic text-crimson">We help tell it.</span>
            </h1>
            <div
              className="mt-6 max-w-2xl space-y-4 text-base leading-relaxed text-cocoa sm:text-lg"
              data-testid="sofia-intro"
            >
              <p>
                Every cause begins with a story — a moment, a struggle, a hope
                that refuses to fade. At MyHeartGives, we believe those stories
                deserve to be heard.
              </p>
              <p>
                I'm Sofia Reyes. Before we talk about plans, visibility, or how
                to reach supporters, I want to hear you. The long nights, the
                small victories, the reason you stepped forward when you could
                have stayed quiet. Your cause matters, and your voice deserves
                a place where people listen with heart.
              </p>
              <p>
                We may not be able to solve every challenge or carry every
                burden, but we can offer something powerful: a space where your
                story is shared with empathy, dignity, and truth.
              </p>
              <p>
                Our role is simple — to walk beside you, help shape your
                message, and make sure others can see what you see, feel what
                you feel, and understand why your cause deserves attention.
              </p>
              <p>
                We are proud to walk alongside trusted partners who share our
                belief that every cause deserves to be heard.
              </p>
              <p className="font-serif text-lg italic text-ink">
                Bring your heart. We'll help it be heard.
              </p>
              <p className="font-serif text-base italic text-ink">
                — Sofia Reyes, Champion of Causes
              </p>
            </div>
            <a
              href="#submit-cause"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-crimson px-7 py-3.5 text-sm font-semibold text-ivory transition-all duration-200 hover:-translate-y-0.5 hover:bg-crimson-deep"
              data-testid="share-your-cause-cta"
            >
              Share Your Cause <Heart className="h-4 w-4" fill="currentColor" />
            </a>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <figure
              className="overflow-hidden rounded-[2rem] border-4 border-gold/60 shadow-2xl"
              data-testid="sofia-portrait"
            >
              <img
                src={SOFIA_IMG}
                alt="Sofia Reyes, Champion of Causes at MyHeartGives"
                className="aspect-[4/5] w-full object-cover object-top sepia-[0.12]"
                loading="eager"
              />
              <figcaption className="flex items-center justify-between bg-white px-6 py-4">
                <span className="font-serif text-lg font-semibold text-ink">Sofia Reyes</span>
                <span className="font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-gold">
                  Champion of Causes
                </span>
              </figcaption>
            </figure>
          </Reveal>
        </div>

        {/* CAUSES OPT-IN */}
        <section id="submit-cause" className="mt-20 scroll-mt-24" data-testid="causes-optin-section">
          <Reveal>
            <OptInForm
              formId="causes-optin"
              heading="Share Your Cause"
              subheading="Tell us who you are and what you're fighting for — Sofia takes it from there."
              source="causes"
              fields={CAUSE_FIELDS}
              buttonLabel="Submit My Cause"
              thankYou="Thank you! Sofia will be in touch soon."
              testidPrefix="causes-optin"
            />
          </Reveal>
        </section>

        {/* FEATURED CAUSES */}
        <section className="mt-20" data-testid="featured-causes-list">
          <Reveal>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-serif text-xl font-bold text-ink sm:text-2xl">
                Featured Causes
              </h2>
              <span
                className="inline-flex items-center gap-1.5 rounded-full border border-[#BCE0D1] bg-pine-soft px-3 py-1 text-xs font-semibold text-pine"
                data-testid="sofia-fit-badge-causes"
              >
                <ShieldCheck className="h-3.5 w-3.5" /> Sofia Fit
              </span>
            </div>
            <p className="mt-2 max-w-2xl text-sm text-cocoa">
              The <strong className="text-pine">Sofia Fit</strong> badge means a
              cause is vetted, launch-ready, and carrying a story we can stand
              behind.
            </p>
          </Reveal>

          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
            {[0, 1].map((i) => (
              <Reveal key={i} delay={0.08 * (i + 1)}>
                <div
                  className="flex h-full items-center justify-center rounded-3xl border-2 border-dashed border-line bg-cream/60 px-6 py-8 text-center"
                  data-testid={`cause-placeholder-${i}`}
                >
                  <p className="font-serif text-base italic text-taupe">
                    Your cause could be next — send Sofia Reyes your story.
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      </section>
    </div>
  );
}
